"use client";

import type { CSSProperties } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { TocHeadingItem } from "./collectTocHeadings";
import { collectTocHeadingsFromDom, DEFAULT_TOC_CONTENT_SELECTOR, normalizeTocLevels, tocHeadingsEqual } from "./collectTocHeadings";
import { applyTocLabelOverrides, collectTocHeadingsFromElements, pickTocHeadingItemsForScan } from "./tocLabels";
import { buildTocNavStyle } from "./tocNavStyle";
import "./tocStyles.css";

/** String literals satisfy static a11y analyzers (e.g. Edge Tools axe) that cannot evaluate JSX booleans. */
const ARIA_EXPANDED_TRUE = {
  "aria-expanded": "true"
} as const;
const ARIA_EXPANDED_FALSE = {
  "aria-expanded": "false"
} as const;
export type { TocHeadingItem } from "./collectTocHeadings";
export { collectTocHeadingsFromBuilderElements } from "./collectTocHeadings";
export type TocIndentStyle = "border" | "indent" | "none";
type TocFallbackContentElement = {
  type?: string;
  tag?: string;
  content?: string;
  domId?: string;
  html?: boolean;
  sourceCode?: string;
  staticHtml?: string;
  compiledCode?: string;
  codeType?: string;
  children?: TocFallbackContentElement[];
};
export type TableOfContentsProps = {
  /** Published DOM id from builder `domId`. */
  htmlId?: string;
  /** Builder node id (`data-pb-node`). */
  dataPbNode?: string;
  title?: string;
  contentSelector?: string;
  /** CSS selector for subtrees to skip when scanning headings. */
  excludeSelector?: string;
  levels?: number[];
  scrollOffset?: number;
  className?: string;
  style?: CSSProperties;
  /** Editor / template thumbnail — skip DOM scan when true. */
  previewStub?: boolean;
  /** Entry-edit preview: rescan when main content headings change. */
  watchContent?: boolean;
  /** Fallback heading list when DOM root is missing (editor sidebar slot edit). */
  fallbackHeadings?: TocHeadingItem[];
  /** Entry builder: scan these elements when DOM has no headings (iframe custom_code). */
  fallbackContentElements?: TocFallbackContentElement[];
  /** Entry-level AI short labels keyed by heading anchor id. */
  labelOverrides?: Record<string, string> | null;
  /** When true, prefer `labelOverrides` over scanned heading text. */
  useAiTocLabels?: boolean;
  sticky?: boolean;
  numbered?: boolean;
  indentStyle?: TocIndentStyle;
  smoothScroll?: boolean;
  highlightActive?: boolean;
  collapseOnMobile?: boolean;
  emptyMessage?: string;
};
const PREVIEW_STUB_ITEMS: TocHeadingItem[] = [{
  id: "preview-h2",
  level: 2,
  text: "Section one"
}, {
  id: "preview-h3",
  level: 3,
  text: "Subsection"
}, {
  id: "preview-h2b",
  level: 2,
  text: "Section two"
}];
function indentPx(level: number, minLevel: number, indentStyle: TocIndentStyle): number {
  if (indentStyle === "none") {
    return 0;
  }
  return Math.max(0, level - minLevel) * 12;
}
export function TableOfContents({
  htmlId,
  dataPbNode,
  title = "On this page",
  contentSelector = DEFAULT_TOC_CONTENT_SELECTOR,
  excludeSelector,
  levels,
  scrollOffset = 80,
  className,
  style,
  previewStub = false,
  watchContent = false,
  fallbackHeadings,
  fallbackContentElements,
  labelOverrides,
  useAiTocLabels = false,
  sticky = true,
  numbered = false,
  indentStyle = "border",
  smoothScroll = true,
  highlightActive = true,
  collapseOnMobile = true,
  emptyMessage
}: TableOfContentsProps) {
  const levelsKey = levels?.join(",") ?? "";
  const normalizedLevels = useMemo(() => normalizeTocLevels(levels), [levelsKey]);
  const minLevel = normalizedLevels[0] ?? 2;
  const builderFallbackHeadings = useMemo(() => collectTocHeadingsFromElements(fallbackContentElements, normalizedLevels), [fallbackContentElements, levelsKey, normalizedLevels]);
  const effectiveFallbackHeadings = fallbackHeadings?.length ? fallbackHeadings : builderFallbackHeadings;
  const [items, setItems] = useState<TocHeadingItem[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [mounted, setMounted] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(!collapseOnMobile);
  const rawHeadingsRef = useRef<TocHeadingItem[]>([]);
  useEffect(() => {
    setMounted(true);
  }, []);
  const applyHeadings = useCallback((next: TocHeadingItem[]) => {
    rawHeadingsRef.current = next;
    const merged = applyTocLabelOverrides(next, {
      tocLabelOverrides: labelOverrides,
      useAiTocLabels,
      builderFallbackItems: effectiveFallbackHeadings
    });
    setItems(current => {
      if (tocHeadingsEqual(current, merged)) {
        return current;
      }
      return merged;
    });
    setActiveId(current_0 => {
      if (merged.length === 0) {
        return current_0 === null ? current_0 : null;
      }
      if (current_0 && merged.some(it => it.id === current_0)) {
        return current_0;
      }
      const first = merged[0]?.id ?? null;
      return current_0 === first ? current_0 : first;
    });
  }, [labelOverrides, useAiTocLabels]);
  const scanDom = useCallback(() => {
    const root = document.querySelector(contentSelector);
    if (!root) {
      if (effectiveFallbackHeadings.length > 0) {
        applyHeadings(effectiveFallbackHeadings);
        return;
      }
      applyHeadings([]);
      return;
    }
    const domItems = collectTocHeadingsFromDom(root, normalizedLevels, excludeSelector);
    const picked = pickTocHeadingItemsForScan({
      builderFallbackItems: effectiveFallbackHeadings,
      domItems,
      labelOverrides,
      useAiTocLabels
    });
    if (picked.length > 0) {
      applyHeadings(picked);
      return;
    }
    applyHeadings([]);
  }, [applyHeadings, contentSelector, effectiveFallbackHeadings, excludeSelector, labelOverrides, normalizedLevels, useAiTocLabels]);
  useEffect(() => {
    if (previewStub) {
      applyHeadings(PREVIEW_STUB_ITEMS);
      setActiveId("preview-h2");
      return;
    }
    if (!mounted) {
      return;
    }
    let cancelled = false;
    let outerFrame = 0;
    let innerFrame = 0;
    const scan = () => {
      if (cancelled) {
        return;
      }
      scanDom();
    };
    scan();
    outerFrame = window.requestAnimationFrame(() => {
      innerFrame = window.requestAnimationFrame(scan);
    });
    return () => {
      cancelled = true;
      window.cancelAnimationFrame(outerFrame);
      window.cancelAnimationFrame(innerFrame);
    };
  }, [applyHeadings, contentSelector, effectiveFallbackHeadings, levelsKey, mounted, previewStub, scanDom]);
  useEffect(() => {
    if (previewStub || !mounted) {
      return;
    }
    applyHeadings(rawHeadingsRef.current);
  }, [applyHeadings, labelOverrides, mounted, previewStub, useAiTocLabels]);
  const findHeadingElement = useCallback((id: string): HTMLElement | null => {
    const direct = document.getElementById(id);
    if (direct) {
      return direct;
    }
    const root_0 = document.querySelector(contentSelector);
    if (!root_0) {
      return null;
    }
    for (const iframe of root_0.querySelectorAll("iframe")) {
      try {
        const found = iframe.contentDocument?.getElementById(id);
        if (found instanceof HTMLElement) {
          return found;
        }
      } catch {
        // ignore inaccessible iframe
      }
    }
    return null;
  }, [contentSelector]);
  useEffect(() => {
    if (previewStub || !watchContent || !mounted) {
      return;
    }
    const root_1 = document.querySelector(contentSelector);
    if (!root_1) {
      return;
    }
    let timer: ReturnType<typeof setTimeout> | undefined;
    const scheduleScan = () => {
      if (timer) {
        clearTimeout(timer);
      }
      timer = setTimeout(() => {
        scanDom();
      }, 250);
    };
    const observer = new MutationObserver(mutations => {
      const relevant = mutations.some(m => {
        if (m.type === "characterData") {
          return true;
        }
        if (m.type === "childList") {
          for (const node of m.addedNodes) {
            if (node instanceof HTMLHeadingElement) {
              return true;
            }
          }
          for (const node_0 of m.removedNodes) {
            if (node_0 instanceof HTMLHeadingElement) {
              return true;
            }
          }
          const target = m.target;
          if (target instanceof Element) {
            if (target.matches("h1,h2,h3,h4,h5,h6")) {
              return true;
            }
            if (target.querySelector("h1,h2,h3,h4,h5,h6")) {
              return true;
            }
          }
        }
        return false;
      });
      if (relevant) {
        scheduleScan();
      }
    });
    observer.observe(root_1, {
      subtree: true,
      childList: true,
      characterData: true
    });
    return () => {
      observer.disconnect();
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [contentSelector, mounted, previewStub, scanDom, watchContent]);
  useEffect(() => {
    if (previewStub || !highlightActive || items.length === 0 || typeof IntersectionObserver === "undefined") {
      return;
    }
    const byId = new Map(items.map(it_0 => [it_0.id, it_0]));
    const observer_0 = new IntersectionObserver(entries => {
      const visible = entries.filter(e => e.isIntersecting).sort((a, b) => (a.target as HTMLElement).offsetTop - (b.target as HTMLElement).offsetTop);
      if (visible.length === 0) {
        return;
      }
      const id_0 = visible[0]?.target.id;
      if (id_0 && byId.has(id_0)) {
        setActiveId(current_1 => current_1 === id_0 ? current_1 : id_0);
      }
    }, {
      rootMargin: `-${scrollOffset}px 0px -55% 0px`,
      threshold: 0
    });
    for (const item of items) {
      const el = findHeadingElement(item.id);
      if (el) {
        observer_0.observe(el);
      }
    }
    return () => observer_0.disconnect();
  }, [findHeadingElement, highlightActive, items, previewStub, scrollOffset]);
  const onJump = useCallback((id_1: string) => {
    if (previewStub) {
      setActiveId(id_1);
      return;
    }
    const el_0 = findHeadingElement(id_1);
    if (!el_0) {
      return;
    }
    const iframe_0 = el_0.ownerDocument.defaultView?.frameElement;
    if (iframe_0 instanceof HTMLIFrameElement) {
      iframe_0.scrollIntoView({
        behavior: smoothScroll ? "smooth" : "auto",
        block: "start"
      });
      el_0.scrollIntoView({
        behavior: smoothScroll ? "smooth" : "auto",
        block: "start"
      });
    } else {
      const top = el_0.getBoundingClientRect().top + window.scrollY - scrollOffset;
      window.scrollTo({
        top,
        behavior: smoothScroll ? "smooth" : "auto"
      });
    }
    setActiveId(id_1);
    if (collapseOnMobile) {
      setMobileOpen(false);
    }
  }, [collapseOnMobile, findHeadingElement, previewStub, scrollOffset, smoothScroll]);
  const navStyle = buildTocNavStyle(style, sticky);
  const resolvedEmpty = emptyMessage?.trim() || (previewStub ? "Headings from the main content appear here on the live site." : "No headings found in the content area.");
  const showTitle = Boolean(title?.trim());
  const mobileLabel = title?.trim() || "On this page";
  return <nav aria-label={mobileLabel} className={["pb-toc", className].filter(Boolean).join(" ")} data-collapse-mobile={collapseOnMobile ? "1" : "0"} data-indent={indentStyle} data-mobile-open={mobileOpen ? "1" : "0"} data-numbered={numbered ? "1" : "0"} data-pb-node={dataPbNode} data-pb-table-of-contents="" data-sticky={sticky ? "1" : "0"} id={htmlId} style={navStyle}>
			{collapseOnMobile ? <button {...mobileOpen ? ARIA_EXPANDED_TRUE : ARIA_EXPANDED_FALSE} className="pb-toc__mobile-trigger" onClick={() => setMobileOpen(open => !open)} type="button">
					<span>{mobileLabel}</span>
					<span aria-hidden className="pb-toc__mobile-trigger-icon">
						▼
					</span>
				</button> : null}
			<div className="pb-toc__panel">
				{showTitle ? <p className="pb-toc__title">{title}</p> : null}
				{items.length === 0 ? <p className="pb-toc__empty">{resolvedEmpty}</p> : <ul className="pb-toc__list">
						{items.map((item_0, index) => {
          const active = highlightActive && item_0.id === activeId;
          // Border + indent modes both nest by heading level (border adds list rail in CSS).
          const padLeft = indentPx(item_0.level, minLevel, indentStyle);
          return <li className="pb-toc__item" key={`${item_0.id}-${index}`}>
									<button className="pb-toc__link" data-active={active ? "1" : "0"} data-level={String(item_0.level)} onClick={() => onJump(item_0.id)} style={padLeft > 0 ? {
              paddingLeft: `${8 + padLeft}px`
            } : undefined} type="button">
										{item_0.text}
									</button>
								</li>;
        })}
					</ul>}
			</div>
		</nav>;
}